import { Node, mergeAttributes } from '@tiptap/core';

const round = value => Math.round(value * 1000) / 1000;

/**
 * Side-by-side column layout: a `columns` block containing two or more `column`
 * blocks (e.g. a two-column signature row). Both nodes round-trip to the server
 * renderer (DocumentGeneration::ProsemirrorRenderer) as
 * `<div class="doc-columns">` / `<div class="doc-column">`, which the print CSS
 * lays out with flexbox — so the editor, the paged.js preview and the WeasyPrint
 * PDF all render the same columns. Class names are shared with the server output.
 *
 * Each column carries an optional `width` (a flex-grow ratio). Columns are resized
 * by dragging the handle on their right edge; the drag adjusts this column and its
 * right neighbour so the row's total width is unchanged. `width` persists in the
 * body JSON and the server emits it as an inline `flex` style.
 */
export const Column = Node.create({
  name: 'column',
  content: 'block+',
  isolating: true,

  addAttributes() {
    return {
      width: {
        default: null,
        parseHTML: element => {
          const grow = parseFloat(element.style.flexGrow);
          return Number.isFinite(grow) && grow > 0 ? grow : null;
        },
        renderHTML: attributes =>
          attributes.width
            ? { style: `flex:${attributes.width} 1 0` }
            : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div.doc-column' }, { tag: 'div[data-column]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { class: 'doc-column', 'data-column': '' }),
      0,
    ];
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      let current = node;

      const dom = document.createElement('div');
      dom.className = 'doc-column';
      dom.setAttribute('data-column', '');

      const content = document.createElement('div');
      content.className = 'doc-column__content';
      dom.appendChild(content);

      const handle = document.createElement('div');
      handle.className = 'doc-column__resize';
      handle.contentEditable = 'false';
      dom.appendChild(handle);

      const applyWidth = n => {
        dom.style.flex = n.attrs.width ? `${n.attrs.width} 1 0` : '';
      };
      applyWidth(current);

      handle.addEventListener('pointerdown', event => {
        if (!editor.isEditable) return;
        const next = dom.nextElementSibling;
        if (!next) return;
        event.preventDefault();
        event.stopPropagation();

        const startX = event.clientX;
        const startW = dom.getBoundingClientRect().width;
        const nextW = next.getBoundingClientRect().width;
        const total = startW + nextW;
        const MIN = 40;
        let leftW = startW;

        const onMove = moveEvent => {
          leftW = Math.min(Math.max(startW + (moveEvent.clientX - startX), MIN), total - MIN);
          dom.style.flex = `${leftW} 1 0`;
          next.style.flex = `${total - leftW} 1 0`;
        };
        const onUp = () => {
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);
          const pos = getPos();
          if (typeof pos !== 'number' || total <= 0) return;
          const leftRatio = round((leftW / total) * 2);
          const rightRatio = round(((total - leftW) / total) * 2);
          const { state } = editor;
          const left = state.doc.nodeAt(pos);
          if (!left) return;
          const nextPos = pos + left.nodeSize;
          const right = state.doc.nodeAt(nextPos);
          const tr = state.tr.setNodeMarkup(pos, undefined, { ...left.attrs, width: leftRatio });
          if (right) tr.setNodeMarkup(nextPos, undefined, { ...right.attrs, width: rightRatio });
          editor.view.dispatch(tr);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
      });

      return {
        dom,
        contentDOM: content,
        update(updated) {
          if (updated.type.name !== 'column') return false;
          current = updated;
          applyWidth(updated);
          return true;
        },
        ignoreMutation(mutation) {
          if (mutation.type === 'selection') return false;
          return !content.contains(mutation.target);
        },
      };
    };
  },
});

export const Columns = Node.create({
  name: 'columns',
  group: 'block',
  // At least two columns — a single-column layout is just a normal paragraph.
  content: 'column column+',
  isolating: true,

  parseHTML() {
    return [{ tag: 'div.doc-columns' }, { tag: 'div[data-columns]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { class: 'doc-columns', 'data-columns': '' }),
      0,
    ];
  },

  addCommands() {
    return {
      insertColumns:
        (count = 2) =>
        ({ chain }) => {
          const columns = Array.from({ length: Math.max(2, count) }, () => ({
            type: 'column',
            content: [{ type: 'paragraph' }],
          }));
          return chain()
            .insertContent({ type: this.name, content: columns })
            .run();
        },
    };
  },
});

export default Columns;
